import { Icon } from '@coast/schemas';
import { isNil } from 'lodash';
import { COLORS } from '@shared/components/blocks/lib/tags/utils';
import { i18n } from '@shared/util/i18n';
import { COMPONENT_TYPE_ICON_MAP, COMPONENT_TYPE_LABEL_MAP } from '../ComponentTypeUtils';
import { ComponentTemplateSection } from './ComponentTemplateSection';
import { ComponentTemplates } from './ComponentTemplates';
import { AuditTagFieldOption } from './options/AuditTagFieldOption';
import { BasicFieldOption } from './options/BasicFieldOption';
import { BatchDateFieldOption } from './options/BatchDateFieldOption';
import { DateRangeFieldOption } from './options/DateRangeFieldOption';
import { FieldOption } from './options/FieldOption';
import { FileFieldOption } from './options/FileFieldOption';
import { RelatedCardFieldOption } from './options/RelatedCardFieldOption';
import { RelatedCardLookupFieldOption } from './options/RelatedCardLookupFieldOption';
import { ReservedIdFieldOption } from './options/ReservedIdFieldOption';
import { ScheduledAutomationFieldOption } from './options/ScheduledAutomationFieldOption';
import { SubformFieldOption } from './options/SubformFieldOption';
import { TagFieldOption } from './options/TagFieldOption';
import { TextFieldOption } from './options/TextFieldOption';
import { TimeTrackerFieldOption } from './options/TimeTrackerFieldOption';

const MAX_SIGNED_INT = Math.pow(2, 31) - 1;

/**
 * Some components have special meaning depending on their id (e.g: 'name' represents the card's title,
 * used across the system. 'startDate' represents the card's start date, used across the system as well)
 */
export const STATIC_COMPONENT_IDS = {
  NAME: 'name',
  START_DATE: 'startDate',
  SEQUENCE_NUMBER: 'sequenceNumber',
  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',
  DELETED_AT: 'deletedAt',
  ASSIGNEES: 'assignees',
};

export function getLabelForComponentType(
  componentType: MBAPI.ComponentType,
  options?: {
    multiselect?: MBAPI.WorkflowTemplate_components_TagComponent['multiselect'];
    multiline?: MBAPI.WorkflowTemplate_components_TextComponent['multiline'];
  },
): string {
  if ('TAG' === componentType) {
    return options?.multiselect
      ? i18n('workflowTemplate.component.type.label.multiSelect')
      : i18n('workflowTemplate.component.type.label.singleSelect');
  }

  if ('AUDIT_TAG' === componentType) {
    return options?.multiselect
      ? i18n('workflowTemplate.component.type.label.multiSelect')
      : i18n('workflowTemplate.component.type.label.singleSelect');
  }

  if ('TEXT' === componentType) {
    return options?.multiline
      ? i18n('workflowTemplate.component.type.label.longText')
      : i18n('workflowTemplate.component.type.label.shortText');
  }
  return COMPONENT_TYPE_LABEL_MAP[componentType];
}

export function getIconForComponentType(
  componentType: MBAPI.ComponentType,
  options?: {
    multiselect?: MBAPI.WorkflowTemplate_components_TagComponent['multiselect'];
    multiline?: MBAPI.WorkflowTemplate_components_TextComponent['multiline'];
  },
): Icon {
  if ('TAG' === componentType) {
    return options?.multiselect ? 'list' : 'single-select-list';
  }
  if ('AUDIT_TAG' === componentType) {
    return options?.multiselect ? 'list' : 'single-select-list';
  }
  if ('TEXT' === componentType) {
    return options?.multiline ? 'multi-line' : 'title-t';
  }
  return COMPONENT_TYPE_ICON_MAP[componentType];
}

const assigneeLabel = i18n('workflowTemplate.component.label.assignee');
const assigneeIcon = getIconForComponentType('PERSON');
const assigneeFieldOption = new ReservedIdFieldOption(assigneeLabel, assigneeIcon, {
  id: STATIC_COMPONENT_IDS.ASSIGNEES,
  type: 'PERSON',
  icon: assigneeIcon,
  label: assigneeLabel,
  placeholder: assigneeLabel,
  required: false,
});

const dueDateLabel = i18n('workflowTemplate.component.label.dueDate');
const dueDateIcon = getIconForComponentType('DATE');
const dueDateFieldOption = new ReservedIdFieldOption(dueDateLabel, dueDateIcon, {
  id: STATIC_COMPONENT_IDS.START_DATE,
  type: 'DATE',
  icon: dueDateIcon,
  label: dueDateLabel,
  placeholder: dueDateLabel,
  required: false,
  allowEntityBatchCreation: false,
});

const multiSelectLabel = getLabelForComponentType('TAG', { multiselect: true });
const multiSelectIcon = getIconForComponentType('TAG', { multiselect: true });
const multiSelectFieldOption = new TagFieldOption(multiSelectLabel, multiSelectIcon, {
  type: 'TAG',
  icon: multiSelectIcon,
  label: multiSelectLabel,
  placeholder: multiSelectLabel,
  multiselect: true,
  addAdditionalValues: true,
  required: false,
  options: [
    {
      label: 'Option 1',
      value: 'option-1',
      backgroundColor: COLORS[0],
      archived: false,
    },
    {
      label: 'Option 2',
      value: 'option-2',
      backgroundColor: COLORS[1],
      archived: false,
    },
  ],
});

const singleSelectLabel = getLabelForComponentType('TAG', { multiselect: false });
const singleSelectIcon = getIconForComponentType('TAG', { multiselect: false });
const singleSelectFieldOption = new TagFieldOption(singleSelectLabel, singleSelectIcon, {
  type: 'TAG',
  icon: singleSelectIcon,
  label: singleSelectLabel,
  placeholder: singleSelectLabel,
  multiselect: false,
  addAdditionalValues: true,
  required: false,
  options: [
    {
      label: 'Option 1',
      value: 'option-1',
      backgroundColor: COLORS[0],
      archived: false,
    },
    {
      label: 'Option 2',
      value: 'option-2',
      backgroundColor: COLORS[1],
      archived: false,
    },
  ],
});

const combinedTagsLabel = getLabelForComponentType('COMBINED_TAGS');
const combinedTagsIcon = getIconForComponentType('COMBINED_TAGS');
const combinedTagsOption = new BasicFieldOption(combinedTagsLabel, combinedTagsIcon, {
  type: 'COMBINED_TAGS',
  icon: combinedTagsIcon,
  label: combinedTagsLabel,
  componentIds: [],
});

const personLabel = getLabelForComponentType('PERSON');
const personIcon = getIconForComponentType('PERSON');
const personOption = new BasicFieldOption(personLabel, personIcon, {
  type: 'PERSON',
  icon: personIcon,
  label: personLabel,
  placeholder: personLabel,
  required: false,
});

const datePickerLabel = i18n('workflowTemplate.component.label.datePicker');
const datePickerIcon = getIconForComponentType('DATE');
const datePickerOption = new BasicFieldOption(datePickerLabel, datePickerIcon, {
  type: 'DATE',
  icon: datePickerIcon,
  label: datePickerLabel,
  placeholder: datePickerLabel,
  required: false,
  allowEntityBatchCreation: false,
});

const dateRangePickerLabel = i18n('workflowTemplate.component.label.dateRangePicker');
const dateRangePickerIcon = getIconForComponentType('DATE_RANGE');
const dateRangePickerOption = new DateRangeFieldOption(dateRangePickerLabel, dateRangePickerIcon, {
  type: 'DATE_RANGE',
  icon: dateRangePickerIcon,
  label: dateRangePickerLabel,
  placeholder: dateRangePickerLabel,
});

const emailLabel = getLabelForComponentType('EMAIL');
const emailIcon = getIconForComponentType('EMAIL');
const emailOption = new BasicFieldOption(emailLabel, emailIcon, {
  type: 'EMAIL',
  icon: emailIcon,
  label: emailLabel,
  placeholder: emailLabel,
  required: false,
});

const urlLabel = getLabelForComponentType('URL');
const urlIcon = getIconForComponentType('URL');
const urlOption = new BasicFieldOption(urlLabel, urlIcon, {
  type: 'URL',
  icon: urlIcon,
  label: urlLabel,
  placeholder: urlLabel,
  required: false,
});

const shortTextLabel = getLabelForComponentType('TEXT', { multiline: false });
const shortTextIcon = getIconForComponentType('TEXT', { multiline: false });
const shortTextOption = new TextFieldOption(shortTextLabel, shortTextIcon, {
  type: 'TEXT',
  icon: shortTextIcon,
  label: shortTextLabel,
  placeholder: shortTextLabel,
  required: false,
  multiline: false,
  maxlength: 255,
});

const longTextLabel = getLabelForComponentType('TEXT', { multiline: true });
const longTextIcon = getIconForComponentType('TEXT', { multiline: true });
const longTextOption = new TextFieldOption(longTextLabel, longTextIcon, {
  type: 'TEXT',
  icon: longTextIcon,
  label: i18n('workflowTemplate.component.label.text'),
  placeholder: i18n('workflowTemplate.component.placeholder.text'),
  required: false,
  multiline: true,
  maxlength: MAX_SIGNED_INT,
});

const numberLabel = getLabelForComponentType('NUMBER');
const numberIcon = getIconForComponentType('NUMBER');
const numberOption = new BasicFieldOption(numberLabel, numberIcon, {
  type: 'NUMBER',
  icon: numberIcon,
  label: numberLabel,
  placeholder: numberLabel,
  required: false,
  format: 'FLOAT',
});

const imageUploadOption = new FileFieldOption('Image Upload', 'camera', {
  type: 'FILE',
  icon: 'camera',
  label: i18n('workflowTemplate.component.label.image'),
  placeholder: i18n('workflowTemplate.component.placeholder.image'),
  acceptedContentTypes: ['image/*'],
  required: false,
});

const fileUploadLabel = getLabelForComponentType('FILE');
const fileUploadIcon = getIconForComponentType('FILE');
const fileUploadOption = new FileFieldOption('File Upload', fileUploadIcon, {
  type: 'FILE',
  icon: fileUploadIcon,
  label: fileUploadLabel,
  placeholder: i18n('workflowTemplate.component.placeholder.file'),
  required: false,
});

const addressFieldLabel = getLabelForComponentType('ADDRESS');
const addressFieldIcon = getIconForComponentType('ADDRESS');
const addressFieldOption = new BasicFieldOption(addressFieldLabel, addressFieldIcon, {
  type: 'ADDRESS',
  icon: addressFieldIcon,
  label: addressFieldLabel,
  placeholder: addressFieldLabel,
  required: false,
});

const shareLocationFieldLabel = getLabelForComponentType('GEOLOCATION');
const shareLocationFieldIcon = getIconForComponentType('GEOLOCATION');
const shareLocationFieldOption = new BasicFieldOption(
  shareLocationFieldLabel,
  shareLocationFieldIcon,
  {
    type: 'GEOLOCATION',
    icon: shareLocationFieldIcon,
    label: shareLocationFieldLabel,
    placeholder: shareLocationFieldLabel,
    required: false,
  },
);

const signatureLabel = getLabelForComponentType('SIGNATURE');
const signatureIcon = getIconForComponentType('SIGNATURE');
const signatureOption = new BasicFieldOption(signatureLabel, signatureIcon, {
  type: 'SIGNATURE',
  icon: signatureIcon,
  label: signatureLabel,
  placeholder: signatureLabel,
  required: false,
});

const todoListLabel = getLabelForComponentType('TODO');
const todoListIcon = getIconForComponentType('TODO');
const todoListOption = new BasicFieldOption(todoListLabel, todoListIcon, {
  type: 'TODO',
  icon: todoListIcon,
  label: todoListLabel,
  placeholder: todoListLabel,
  required: false,
});

const relatedCardLabel = getLabelForComponentType('RELATED_CARD');
const relatedCardIcon = getIconForComponentType('RELATED_CARD');
const relatedCardOption = new RelatedCardFieldOption(relatedCardLabel, relatedCardIcon, {
  type: 'RELATED_CARD',
  icon: relatedCardIcon,
  label: relatedCardLabel,
  placeholder: relatedCardLabel,
  required: false,
});

const relatedCardLookupLabel = getLabelForComponentType('RELATED_CARD_LOOKUP');
const relatedCardLookupIcon = getIconForComponentType('RELATED_CARD_LOOKUP');
export const relatedCardLookupOption = new RelatedCardLookupFieldOption(
  relatedCardLookupLabel,
  relatedCardLookupIcon,
  {
    type: 'RELATED_CARD_LOOKUP',
    icon: relatedCardLookupIcon,
    label: relatedCardLookupLabel,
    placeholder: relatedCardLookupLabel,
    required: false,
  },
);

const scheduledAutomationLabel = getLabelForComponentType('SCHEDULED_AUTOMATION');
const scheduledAutomationIcon = getIconForComponentType('SCHEDULED_AUTOMATION');
const scheduledAutomationOption = new ScheduledAutomationFieldOption(
  scheduledAutomationLabel,
  scheduledAutomationIcon,
  {
    type: 'SCHEDULED_AUTOMATION',
    icon: scheduledAutomationIcon,
    label: scheduledAutomationLabel,
    placeholder: scheduledAutomationLabel,
    required: false,
  },
);

const subFormLabel = getLabelForComponentType('SUBFORM');
const subFormIcon = getIconForComponentType('SUBFORM');
const subFormOption = new SubformFieldOption(subFormLabel, subFormIcon, {
  type: 'SUBFORM',
  icon: subFormIcon,
  label: subFormLabel,
  placeholder: subFormLabel,
  required: false,
  workflowTemplateIds: [],
});

const timeTrackerLabel = getLabelForComponentType('TIME_TRACKER');
const timeTrackerIcon = getIconForComponentType('TIME_TRACKER');
const timeTrackerOption = new TimeTrackerFieldOption(timeTrackerLabel, timeTrackerIcon, {
  type: 'TIME_TRACKER',
  icon: getIconForComponentType('TIME_TRACKER'),
  label: getLabelForComponentType('TIME_TRACKER'),
  startButtonLabel: i18n('workflowTemplate.component.type.label.timeTrackerStart'),
  endButtonLabel: i18n('workflowTemplate.component.type.label.timeTrackerEnd'),
  requiresGeolocation: false,
  autoStart: false,
});

const referencedInLabel = getLabelForComponentType('REFERENCED_IN');
const referencedInIcon = getIconForComponentType('REFERENCED_IN');
const referencedInOption = new BasicFieldOption(referencedInLabel, referencedInIcon, {
  type: 'REFERENCED_IN',
});

const inputButtonLabel = getLabelForComponentType('INPUT_BUTTON');
const inputButtonIcon = getIconForComponentType('INPUT_BUTTON');
const inputButtonOption = new BasicFieldOption(inputButtonLabel, inputButtonIcon, {
  type: 'INPUT_BUTTON',
  buttonText: i18n('builder.inputButtonOptions.defaultButtonText'),
});

const entityBatchLabel = getLabelForComponentType('ENTITY_BATCH');
const entityBatchIcon = getIconForComponentType('ENTITY_BATCH');
const entityBatch: FieldOption = {
  title: entityBatchLabel,
  icon: entityBatchIcon,
  component: {
    type: 'ENTITY_BATCH',
    label: entityBatchLabel,
    icon: entityBatchIcon,
    id: 'entityBatch',
    isServerManaged: true,
    readonly: true,
    locked: true,
  },
  matchesComponent: function (
    component: MBAPI.WorkflowTemplate_components,
    _reservedIds: string[],
  ): boolean {
    return component.type === 'ENTITY_BATCH';
  },
  canBeAdded: function (components: MBAPI.WorkflowTemplate_components[]): boolean {
    return isNil(components.find(c => c.type === 'ENTITY_BATCH'));
  },
};

const batchDateLabel = i18n('workflowTemplate.component.type.label.dateAndRepeat');
const batchDateIcon = getIconForComponentType('DATE');
const batchDateOption = new BatchDateFieldOption(
  i18n('workflowTemplate.component.label.batchDate'),
  batchDateIcon,
  {
    type: 'DATE',
    icon: batchDateIcon,
    label: batchDateLabel,
    placeholder: batchDateLabel,
    required: false,
    allowEntityBatchCreation: true,
  },
  [entityBatch],
);

const suggestedSection: ComponentTemplateSection = {
  title: i18n('workflowTemplate.component.section.suggested'),
  subformsOnly: false,
  items: [
    assigneeFieldOption,
    dueDateFieldOption,
    multiSelectFieldOption,
    singleSelectFieldOption,
    batchDateOption,
  ],
};

const basicSection: ComponentTemplateSection = {
  title: 'Basic',
  subformsOnly: false,
  items: [
    personOption,
    datePickerOption,
    dateRangePickerOption,
    emailOption,
    urlOption,
    shortTextOption,
    longTextOption,
    numberOption,
    imageUploadOption,
    fileUploadOption,
    addressFieldOption,
    shareLocationFieldOption,
    signatureOption,
    relatedCardOption,
    relatedCardLookupOption,
    scheduledAutomationOption,
    todoListOption,
    subFormOption,
    combinedTagsOption,
    timeTrackerOption,
    referencedInOption,
    inputButtonOption,
  ],
};

const auditTagLabel = getLabelForComponentType('AUDIT_TAG', { multiselect: false });
const auditTagIcon = getIconForComponentType('AUDIT_TAG', { multiselect: false });
const auditTagOption = new AuditTagFieldOption(auditTagLabel, auditTagIcon, {
  type: 'AUDIT_TAG',
  icon: auditTagIcon,
  label: auditTagLabel,
  placeholder: auditTagLabel,
  required: false,
  multiselect: false,
  addAdditionalValues: true,
  options: [
    {
      label: 'Option 1',
      value: 'option-1',
      backgroundColor: COLORS[0],
      archived: false,
    },
    {
      label: 'Option 2',
      value: 'option-2',
      backgroundColor: COLORS[1],
      archived: false,
    },
  ],
});

const auditTagMultiSelectLabel = getLabelForComponentType('AUDIT_TAG', { multiselect: true });
const auditTagMultiSelectIcon = getIconForComponentType('AUDIT_TAG', { multiselect: true });
const auditTagMultiSelectOption = new AuditTagFieldOption(
  auditTagMultiSelectLabel,
  auditTagMultiSelectIcon,
  {
    type: 'AUDIT_TAG',
    icon: auditTagMultiSelectIcon,
    label: auditTagMultiSelectLabel,
    placeholder: auditTagMultiSelectLabel,
    required: false,
    multiselect: true,
    addAdditionalValues: true,
    options: [
      {
        label: 'Option 1',
        value: 'option-1',
        backgroundColor: COLORS[0],
        archived: false,
      },
      {
        label: 'Option 2',
        value: 'option-2',
        backgroundColor: COLORS[1],
        archived: false,
      },
    ],
  },
);

const auditTextLabel = getLabelForComponentType('AUDIT_TEXT');
const auditTextIcon = getIconForComponentType('AUDIT_TEXT');
const auditTextOption = new BasicFieldOption(auditTextLabel, auditTextIcon, {
  type: 'AUDIT_TEXT',
  icon: auditTextIcon,
  label: auditTextLabel,
  placeholder: auditTextLabel,
  required: false,
  multiline: true,
  maxlength: MAX_SIGNED_INT,
});

const auditCheckboxLabel = getLabelForComponentType('AUDIT_CHECKBOX');
const auditCheckboxIcon = getIconForComponentType('AUDIT_CHECKBOX');
const auditCheckboxComponent = new BasicFieldOption(auditCheckboxLabel, auditCheckboxIcon, {
  type: 'AUDIT_CHECKBOX',
  icon: auditCheckboxIcon,
  label: auditCheckboxLabel,
  placeholder: auditCheckboxLabel,
  required: false,
});

const staticTextLabel = getLabelForComponentType('STATIC_TEXT');
const staticTextIcon = getIconForComponentType('STATIC_TEXT');
const staticTextComponent = new BasicFieldOption(staticTextLabel, staticTextIcon, {
  type: 'STATIC_TEXT',
  text: staticTextLabel,
});

const qaSection: ComponentTemplateSection = {
  title: i18n('workflowTemplate.component.section.questionAnswerField'),
  subformsOnly: true,
  items: [
    auditTagOption,
    auditTagMultiSelectOption,
    auditTextOption,
    auditCheckboxComponent,
    staticTextComponent,
  ],
};

export const defaultComponentTemplateOptions = new ComponentTemplates({
  templateSections: [suggestedSection, basicSection, qaSection],
  reservedIds: [
    STATIC_COMPONENT_IDS.ASSIGNEES,
    STATIC_COMPONENT_IDS.START_DATE,
    STATIC_COMPONENT_IDS.SEQUENCE_NUMBER,
    STATIC_COMPONENT_IDS.CREATED_AT,
    STATIC_COMPONENT_IDS.UPDATED_AT,
    STATIC_COMPONENT_IDS.DELETED_AT,
  ],
  nonEditableLabelComponentIds: [],
  nonEditablePlaceholderComponentIds: [
    STATIC_COMPONENT_IDS.CREATED_AT,
    STATIC_COMPONENT_IDS.UPDATED_AT,
    STATIC_COMPONENT_IDS.DELETED_AT,
    STATIC_COMPONENT_IDS.SEQUENCE_NUMBER,
  ],
});
